<template>
  <div class="card mb-4">
    <div class="card-body">
      <h5 class="mb-3">{{ indicador.titulo }}</h5>

      <b-form-file
        v-model="arquivo"
        browse-text="Selecionar"
        placeholder="Selecione o arquivo..."
        drop-placeholder="Solte o arquivo aqui..."
        accept=".xls,.xlsx,.csv"
        :disabled="isLoading"
        @input="previewArquivo"
      ></b-form-file>

      <div v-if="preview.length" class="table-responsive mt-4">
        <b-alert show variant="info" class="mb-3">
          <i class="icofont-info-circle mr-2"></i>

          Esta prévia exibe apenas
          <strong>2 registros</strong>
          do arquivo para conferência. Todos os registros serão importados.
        </b-alert>
        <div class="preview-header">
          <div class="preview-confirmacao">
            <b-button
              class="btn-confirmar"
              size="sm"
              :class="{ 'is-confirmed': arquivoConfirmado }"
              :disabled="isLoading || arquivoConfirmado"
              @click="confirmarArquivo"
            >
              <i
                :class="
                  arquivoConfirmado ? 'icofont-check-circled' : 'icofont-check'
                "
              ></i>

              {{
                arquivoConfirmado ? 'Arquivo confirmado' : 'Confirmar arquivo'
              }}
            </b-button>

            <small class="texto-confirmacao">
              {{
                arquivoConfirmado
                  ? 'Arquivo confirmado e pronto para importação.'
                  : 'É necessário confirmar o arquivo antes de realizar a importação.'
              }}
            </small>
          </div>

          <h6 class="mb-0">Prévia do arquivo</h6>
        </div>

        <table class="table table-sm table-bordered table-preview">
          <thead>
            <tr>
              <th v-for="(coluna, index) in preview[0]" :key="index">
                {{ coluna }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(linha, index) in preview.slice(1)" :key="index">
              <td v-for="(coluna, i) in linha" :key="i">
                {{ coluna }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        class="btn btn-primary mt-3"
        :disabled="isLoading || !arquivoConfirmado"
        @click="importar"
      >
        <template v-if="isLoading">
          <b-spinner small class="mr-2"></b-spinner>
          Importando...
        </template>

        <template v-else> Importar </template>
      </button>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  name: 'ImportadorListaNominal',

  props: {
    indicador: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      arquivo: null,
      preview: [],
      isLoading: false,
      arquivoConfirmado: false,
    }
  },

  methods: {
    /**
     * Lê a primeira aba da planilha.
     */
    lerArquivo(arquivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result)

            const workbook = XLSX.read(data, {
              type: 'array',
            })

            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]

            const linhas = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: '',
            })

            resolve(linhas)
          } catch (error) {
            reject(error)
          }
        }

        reader.onerror = () => {
          reject(new Error('Erro ao ler o arquivo.'))
        }

        reader.readAsArrayBuffer(arquivo)
      })
    },

    /**
     * Verifica se a planilha pertence ao indicador.
     */
    arquivoEhDoIndicador(linhas) {
      const normalizar = (valor) =>
        String(valor || '')
          .trim()
          .toLowerCase()

      const identificador = normalizar(linhas?.[13]?.[0])

      return identificador.includes(normalizar(this.indicador.identificador))
    },

    /**
     * Verifica se a planilha pertence ao município autenticado.
     */
    arquivoEhDoMunicipio(linhas) {
      const municipioArquivo = String(linhas?.[9]?.[0] || '')

      const match = municipioArquivo.match(/Município:\s*(\d{6})\s*\//)

      if (!match) {
        return false
      }

      const codIbgeArquivo = match[1]

      const codIbgeUsuario = String(this.$store.state.cod_ibge || '').slice(
        0,
        6
      )

      return codIbgeArquivo === codIbgeUsuario
    },

    /**
     * Monta a prévia com cabeçalho e dois registros.
     */
    montarPreview(linhas) {
      return linhas
        .slice(16, 19)
        .map((linha) => linha.slice(0, this.indicador.colunasPreview))
        .filter((linha) => linha.some((coluna) => String(coluna).trim() !== ''))
    },

    /**
     * Valida o arquivo e gera a prévia.
     */
    async previewArquivo() {
      this.preview = []
      this.arquivoConfirmado = false

      if (!this.arquivo) return

      const arquivoSelecionado = this.arquivo

      try {
        const linhas = await this.lerArquivo(arquivoSelecionado)

        // Evita atualizar a prévia de um arquivo
        // que foi substituído durante a leitura.
        if (this.arquivo !== arquivoSelecionado) return

        if (!this.arquivoEhDoMunicipio(linhas)) {
          this.arquivo = null

          this.$toast?.error?.(
            'O arquivo selecionado não pertence ao município.'
          )

          return
        }

        if (!this.arquivoEhDoIndicador(linhas)) {
          this.arquivo = null

          this.$toast?.error?.(
            `Arquivo inválido. Selecione o arquivo do indicador ${this.indicador.titulo}.`
          )

          return
        }

        this.preview = this.montarPreview(linhas)
      } catch (error) {
        console.error(error)

        if (this.arquivo !== arquivoSelecionado) return

        this.arquivo = null
        this.$toast?.error?.('Erro ao ler o arquivo.')
      }
    },

    /**
     * Confirma que o usuário conferiu a prévia.
     */
    confirmarArquivo() {
      if (!this.arquivo || !this.preview.length) return

      this.arquivoConfirmado = true
    },

    /**
     * Envia a planilha para a API.
     */
    async importar() {
      if (!this.arquivo || !this.preview.length) {
        this.$toast?.error?.('Selecione um arquivo válido.')
        return
      }

      if (!this.arquivoConfirmado) {
        this.$toast?.error?.('Confirme a prévia do arquivo antes de importar.')
        return
      }

      if (this.isLoading) return

      const formData = new FormData()

      formData.append('co_ibge', this.$store.state.cod_ibge)
      formData.append('arquivo', this.arquivo)

      this.isLoading = true

      try {
        await this.$api.post(
          `/app/lista-nominal/importar/${this.indicador.id}`,
          formData
        )

        this.$toast?.success?.('Arquivo importado com sucesso.')
      } catch (error) {
        console.error(error)

        this.$toast?.error?.('Erro ao importar o arquivo.')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.table-responsive {
  display: inherit;
  margin-bottom: 0;
}

.preview-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 12px;

  .preview-confirmacao {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .texto-confirmacao {
      font-size: 12px;
      color: #6c757d;
      line-height: 1.4;
    }
  }

  .btn-confirmar {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    padding: 5px 12px;
    border: 1px solid #0d6efd;
    border-radius: 6px;

    background: transparent;
    color: #0d6efd;

    font-size: 12px;
    font-weight: 500;
    text-transform: none;

    box-shadow: none;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #eaf3ff;
      border-color: #0a58ca;
      color: #0a58ca;
    }

    &:focus {
      box-shadow: none;
    }

    &.is-confirmed {
      background: #e8f5e9;
      border-color: #c8e6c9;
      color: #2e7d32;
      opacity: 1;
      cursor: default;
    }

    i {
      font-size: 13px;
    }
  }
}
</style>
